import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  Appointment,
  LocationData,
  LocationDataGeocoded,
  HTMLElementEvent,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { dayjs } from '@sc/utils';
import { AppointmentEndTimeValidator } from '../appointment-date-picker';
import { TranslateModule } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { InputTextComponent } from '../input-text';
import { ButtonComponent } from '../button';
import { IconComponent } from '../icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AppointmentDatePickerComponent } from '../appointment-date-picker';
import { NgIf } from '@angular/common';

@Component({
  selector: 'sc-appointment-light-new',
  styleUrls: ['./appointment-light-new.component.scss'],
  encapsulation: ViewEncapsulation.None,
  template: `
    <ng-container [formGroup]="getAppointmentForm()" *ngIf="form">
      <sc-appointment-date-picker
        class="mb-l"
        [form]="getAppointmentForm()"
        [start]="getAppointmentForm()?.get('time')"
        [end]="getAppointmentForm()?.get('endTime')"
        [timezone]="getAppointmentForm()?.get('timezone')?.value"
        [outsideIntervention]="
          getAppointmentForm()?.get('outsideIntervention')?.value
        "
      >
      </sc-appointment-date-picker>

      <div class="form-data">
        <mat-label>{{
          'AppointmentLightNew.outsideInterventionLabel' | translate
        }}</mat-label>
        <mat-radio-group (change)="outsideInterventionChange($event)">
          <mat-radio-button
            [value]="false"
            [checked]="!getInterventionTypeValue()"
            >{{ 'AppointmentLightNew.garageIntervention' | translate }}
          </mat-radio-button>
          <mat-radio-button
            [checked]="getInterventionTypeValue()"
            [value]="true"
            >{{ 'AppointmentLightNew.outsideIntervention' | translate }}
          </mat-radio-button>
        </mat-radio-group>
      </div>
      <div class="form-data">
        <div class="replacement-vehicle-filter">
          <div class="filters-summary-rv">
            <mat-slide-toggle
              [disableRipple]="true"
              [hideIcon]="true"
              formControlName="isReplacementVehicleSelected"
              (change)="onChangeReplacementVehicle($event?.checked)"
            ></mat-slide-toggle>
            <sc-icon name="car"></sc-icon>
            <span class="filter-rv-text">{{
              'AppointmentLightNew.interventionLabel' | translate
            }}</span>
          </div>
        </div>
      </div>
      <span
        class="address-bloc grey-bg"
        *ngIf="getInterventionTypeValue() === true"
      >
        <sc-button
          *ngIf="uknonwnAddressSelected"
          buttotType="secondary"
          leftIcon="close"
          class="close-details-btn"
          (click)="uknonwnAddressSelected = false"
        >
          {{ 'AppointmentLightNew.unknownAdressButton' | translate }}
        </sc-button>

        <div class="address-bloc" *ngIf="uknonwnAddressSelected">
          <div class="form-row">
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="'AppointmentLightNew.streetNum' | translate"
                [disabled]="unknownAddrHasNoNumStreet"
                [value]="modifiedLocation?.addressElements.streetNumber"
                (input)="onChangeStreetNumber($event)"
              ></sc-input-text>
            </div>
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="'AppointmentLightNew.streetType' | translate"
                [value]="modifiedLocation?.addressElements.streetSuffix"
                (input)="onChangeStreetSuffix($event)"
              ></sc-input-text>
            </div>
          </div>
          <div class="form-row">
            <div class="form-data">
              <mat-checkbox
                [checked]="unknownAddrHasNoNumStreet"
                (change)="onChangeStreetNumCheck($event)"
                >{{
                  'AppointmentLightNew.checkboxNoStreetNum' | translate
                }}</mat-checkbox
              >
            </div>
          </div>
          <div class="form-row">
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="'AppointmentLightNew.streetName' | translate"
                [value]="modifiedLocation?.addressElements.streetName"
                (input)="onChangeStreetName($event)"
              ></sc-input-text>
            </div>
          </div>
          <div class="form-row">
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="'AppointmentLightNew.postalCode' | translate"
                [value]="modifiedLocation?.addressElements?.postalCode"
                (input)="onChangePostalCode($event)"
              ></sc-input-text>
            </div>
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="'AppointmentLightNew.cityLabel' | translate"
                [value]="modifiedLocation?.addressElements?.city"
                (input)="onChangeCity($event)"
              ></sc-input-text>
            </div>
          </div>
        </div>

        <div class="address-bloc">
          <div *ngIf="!uknonwnAddressSelected">
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="'AppointmentLightNew.addressLabel' | translate"
                suggestionsLabelName="address"
                [value]="formatSelectedAddress()"
                [hint]="getAutocompleteHintText()"
                [suggestions]="suggestedLocationsData"
                [forceDisplayError]="isAddressInputError"
                (input)="onAddressChangeValue($event)"
                (updateSuggestions)="onUpdateAddressSuggestions($event)"
                (selectSuggestion)="onSelectAddress($event)"
              ></sc-input-text>
            </div>
            <div class="form-data">
              <div class="city-label">
                {{ 'AppointmentLightNew.cityLabel' | translate }}
              </div>
              <span *ngIf="selectedSearchLocationGeoCodedData">
                {{ getCityLabel() }}
              </span>
            </div>
          </div>
        </div>
      </span>
      <sc-button
        class="validation-button"
        buttonType="primary"
        [disabled]="form?.invalid"
        (click)="onValidate()"
      >
        {{ 'AppointmentLightNew.createBtn' | translate }}
      </sc-button>

      <sc-button
        class="new-apt-button"
        buttonType="tertiary"
        (click)="onReturnClick()"
      >
        {{ 'AppointmentLightNew.returnBtn' | translate }}
      </sc-button>
    </ng-container>
  `,
  imports: [
    NgIf,
    ReactiveFormsModule,
    AppointmentDatePickerComponent,
    MatFormFieldModule,
    MatRadioModule,
    MatSlideToggleModule,
    IconComponent,
    ButtonComponent,
    InputTextComponent,
    MatCheckboxModule,
    TranslateModule,
  ],
})
export class AppointmentLightNewComponent implements OnInit {
  @Input() date: Date;
  @Input() existingAppointment: Appointment;
  @Input() disabledReplacementVehicle: boolean;
  @Input() set suggestedLocationData(locations: LocationData[]) {
    this.suggestedLocationsData = [];
    if (locations) {
      this.suggestedLocationsData = locations;
      this.pushUnknownAddress();
      this.changeDetector.detectChanges();
    } else {
      this.pushUnknownAddress();
      this.changeDetector.detectChanges();
    }
  }
  @Input() set selectedGeocodedAddress(location: LocationDataGeocoded) {
    if (location?.isCompleteAddress) {
      this.selectedAddressStr = location.address;
      this.selectedSearchLocationGeoCodedData = location;
      this.getInterventionLocationForm()?.setValue(location);
    }

    this.isAddressInputError = !location?.isCompleteAddress;
    this.changeDetector.detectChanges();
  }

  @Output() addressSelectEvent = new EventEmitter<LocationData>();
  @Output() addressStrChange = new EventEmitter<string>();
  @Output() returnEvent = new EventEmitter();
  @Output() lightAptValidetEvent = new EventEmitter();

  form: FormGroup;
  selectedSearchLocationGeoCodedData: LocationDataGeocoded;
  selectedAddressStr: string;
  isAddressInputError: boolean;
  unknownAddrHasNoNumStreet: boolean;
  suggestedLocationsData: LocationData[] = [];
  uknonwnAddressSelected = false;
  modifiedLocation: LocationDataGeocoded;
  showUnknownAddressForm: boolean;

  constructor(
    private readonly formBuilder: FormBuilder,
    public translateService: TranslatorService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const now = new Date();
    const aptTime = dayjs(this.date).hour(now.getHours());

    this.form = new FormGroup({
      appointment: this.formBuilder.group(
        {
          id: [null],
          time: [aptTime?.toISOString(), [Validators.required]],
          endTime: [null, [Validators.required]],
          fitterId: [this.existingAppointment?.fitterId, [Validators.required]],
          outsideIntervention: [
            this.existingAppointment?.outsideIntervention,
            [Validators.required],
          ],

          interventionLocation: [null],
          isReplacementVehicleSelected: [
            this.existingAppointment?.isReplacementVehicleSelected,
          ],
          timezone: [this.existingAppointment?.timezone],
          brandId: [this.existingAppointment?.brandId],
        },
        {
          validators: AppointmentEndTimeValidator.consistency(
            'time',
            'endTime'
          ),
        }
      ),
    });
  }

  getAppointmentForm(): FormGroup {
    return this.form?.get('appointment') as FormGroup;
  }

  getInterventionTypeValue() {
    return this.getAppointmentForm()?.get('outsideIntervention')?.value;
  }

  getInterventionLocationForm() {
    return this.getAppointmentForm()?.get(
      'interventionLocation'
    ) as AbstractControl;
  }

  getOutsideInterventionForm() {
    return this.getAppointmentForm()?.get(
      'outsideIntervention'
    ) as AbstractControl;
  }

  outsideInterventionChange($event) {
    if ($event?.value === false) {
      this.getOutsideInterventionForm()?.setValue($event?.value);
      this.getInterventionLocationForm().clearValidators();
      this.getInterventionLocationForm().updateValueAndValidity();
      this.changeDetector.detectChanges();
    } else {
      this.getOutsideInterventionForm()?.setValue(true);
      this.getAppointmentForm()
        ?.get('isReplacementVehicleSelected')
        ?.setValue(false);
      this.getInterventionLocationForm().setValidators([Validators.required]);
      this.getInterventionLocationForm().updateValueAndValidity();
      this.changeDetector.detectChanges();
    }
  }

  onChangeReplacementVehicle($event) {
    this.getAppointmentForm()
      ?.get('outsideIntervention')
      ?.patchValue($event?.value);
  }

  onReturnClick() {
    this.returnEvent.emit();
  }

  onSelectAddress(locationData: LocationData) {
    if (locationData.placeId) {
      this.modifiedLocation = null;
      this.uknonwnAddressSelected = false;

      this.addressSelectEvent.emit(locationData);
    } else {
      (this.modifiedLocation = {
        placeId: null,
        locationProvider: 'NONE',
        isKnownAddress: false,
        isCompleteAddress: false,
        addressElements: { country: 'fr' },
        geoCoordinates: null,
        address: '',
      }),
        (this.uknonwnAddressSelected = true);
    }
  }

  formatSelectedAddress() {
    return [
      this.selectedSearchLocationGeoCodedData?.addressElements.streetNumber,
      this.selectedSearchLocationGeoCodedData?.addressElements.streetName,
    ].join(' ');
  }

  onAddressChangeValue($event) {
    const value = $event?.target?.value;
    this.isAddressInputError = !value || value !== this.selectedAddressStr;
  }

  getAutocompleteHintText(): string {
    return this.selectedSearchLocationGeoCodedData?.isCompleteAddress !==
      true && this.isAddressInputError
      ? this.translateService.getInstantTranslation(
          'AppointmentLightNew.addressHint'
        )
      : '';
  }

  onUpdateAddressSuggestions(value: string) {
    if (value !== this.selectedAddressStr) {
      this.addressStrChange.emit(value);
    }
  }

  getCityLabel() {
    return this.selectedSearchLocationGeoCodedData?.addressElements?.postalCode
      ? this.selectedSearchLocationGeoCodedData?.addressElements?.postalCode +
          ' ' +
          this.selectedSearchLocationGeoCodedData?.addressElements?.city
      : this.selectedSearchLocationGeoCodedData?.addressElements?.city;
  }

  onChangeStreetNumber($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.streetNumber = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
      this.updateFormModifiedAddress();
    }
  }

  onChangeStreetSuffix($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.streetSuffix = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
      this.updateFormModifiedAddress();
    }
  }

  onChangeStreetName($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.streetName = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
      this.updateFormModifiedAddress();
    }
  }

  onChangePostalCode($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.postalCode = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
      this.updateFormModifiedAddress();
    }
  }

  onChangeCity($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.city = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
      this.updateFormModifiedAddress();
    }
  }

  updateFormModifiedAddress() {
    const isComplete =
      !!this?.modifiedLocation?.addressElements?.city &&
      !!this?.modifiedLocation?.addressElements?.postalCode &&
      !!this?.modifiedLocation?.addressElements?.streetSuffix &&
      !!this?.modifiedLocation?.addressElements?.streetName &&
      (!!this?.modifiedLocation?.addressElements?.streetNumber ||
        this.unknownAddrHasNoNumStreet);
    if (isComplete) {
      this.modifiedLocation = {
        ...this.modifiedLocation,
        address: this.getAddress(),
      };
      this.form
        .get('appointment')
        ?.get('interventionLocation')
        ?.setValue(this.modifiedLocation);
    }
  }

  getAddress() {
    return this.modifiedLocation?.addressElements?.streetNumber
      ? this.modifiedLocation?.addressElements?.streetNumber +
          ', ' +
          this.modifiedLocation?.addressElements?.streetSuffix +
          ' ' +
          this.modifiedLocation?.addressElements?.streetName
      : this.modifiedLocation?.addressElements?.streetSuffix +
          ' ' +
          this.modifiedLocation?.addressElements?.streetName;
  }

  onChangeStreetNumCheck(newValue) {
    if (newValue?.checked && this.modifiedLocation) {
      this.modifiedLocation.addressElements.streetNumber = '';
    }
  }

  pushUnknownAddress() {
    this.suggestedLocationsData.push({
      address: this.translateService.getInstantTranslation(
        'AppointmentLightNew.unknownAddressValue'
      ),
      isCompleteAddress: false,
      isKnownAddress: false,
      locationProvider: 'NONE',
      placeId: null,
    });
  }

  onValidate() {
    this.lightAptValidetEvent.emit(this.form.getRawValue());
  }
}
